<script setup lang="ts">
import { useRouter } from "vue-router";
import DarkModeToggle from '@/components/layout/DarkModeToggle.vue';
import Search from '@/components/search/SearchField.vue';
import UserHeaderMenu from '@/components/navigation/UserHeaderMenu.vue';
import SupportHeaderMenu from '@/components/navigation/SupportHeaderMenu.vue';
import routeManager from "@/router/route-manager";
import { useMenuPermissions } from "@/composables/menu-permissions";
import NotificationsHeaderMenu from "@/components/navigation/NotificationsHeaderMenu.vue";
import { useMobileSidebarMenu } from "@/composables/useMobileSidebarMenu";

const { showMainSearch } = useMenuPermissions();
const { openMenu } = useMobileSidebarMenu();

const router = useRouter();

async function navigateToSecurityDetails ( ticker: string ) {
  await router.push( routeManager.buildSecurityPath( ticker ) );
}

async function navigateToSectorDetails ( symbol: string ) {
  await router.push( routeManager.sectorDetailsRouteBySymbol( symbol ) );
}
</script>

<template>
  <div class="flex w-full max-w-full min-h-12 items-center gap-2 py-1.5 sm:gap-3">
    <button
      type="button"
      class="btn btn-ghost btn-square shrink-0 touch-manipulation lg:hidden min-h-11 min-w-11 rounded-lg"
      aria-label="Open navigation menu"
      @click="openMenu"
    >
      <i class="pi pi-bars text-xl" />
    </button>

    <router-link
      :to="routeManager.homePath()"
      class="tmi-logo mr-1 hidden h-9 w-32 shrink-0 sm:mr-2 lg:block"
    />

    <div
      v-if="showMainSearch"
      class="prominent-search flex min-w-0 flex-1 justify-center lg:justify-stretch"
    >
      <Search
        class="flex w-full max-w-[min(100%,20rem)] sm:max-w-md lg:max-w-none"
        :stock-click-handler="navigateToSecurityDetails"
        :etf-click-handler="navigateToSecurityDetails"
        :sector-click-handler="navigateToSectorDetails"
        :supports-content="true"
      />
    </div>
    <div v-else class="min-w-0 flex-1" />

    <div
      class="ms-auto flex shrink-0 items-center justify-end gap-1 sm:gap-2 lg:ps-2"
    >
      <NotificationsHeaderMenu />
      <SupportHeaderMenu />
      <UserHeaderMenu />
      <DarkModeToggle />
    </div>
  </div>
</template>

<style scoped>
/* Full-width slot + slightly larger field on desktop (PrimeVue AutoComplete) */
.prominent-search :deep(.p-autocomplete),
.prominent-search :deep(.p-autocomplete-fluid) {
  width: 100%;
}

.prominent-search :deep(input) {
  min-height: 2.75rem;
  font-size: 1rem;
  line-height: 1.4;
}

@media (min-width: 1024px) {
  .prominent-search :deep(input) {
    min-height: 3rem;
    font-size: 1.0625rem;
  }
}
</style>